import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CalloutContext, useOkapiKy, useStripes } from '@folio/stripes/core';
import GeneralConfigForm from './GeneralConfigForm';

const CONFIGS = {
  MAX_QUERY_TIMEOUT:          { name: 'Max Query Timeout',          defaultVal: '60' },
  REPORT_CACHE_TTL:           { name: 'Report Cache TTL',           defaultVal: '300' },
  MAX_EXPORT_ROWS:            { name: 'Max Export Rows',            defaultVal: '10000' },
  ENABLE_QUERY_VALIDATION:    { name: 'Enable Query Validation',    defaultVal: 'true' },
  ENABLE_SCHEDULED_REPORTS:   { name: 'Enable Scheduled Reports',   defaultVal: 'true' },
  EMAIL_NOTIFICATIONS:        { name: 'Email Notifications',        defaultVal: 'true' },
  DASHBOARD_REFRESH_INTERVAL: { name: 'Dashboard Refresh Interval', defaultVal: '300000' },
  SMTP_HOST:     { name: 'SMTP Host',     defaultVal: '' },
  SMTP_PORT:     { name: 'SMTP Port',     defaultVal: '587' },
  SMTP_USERNAME: { name: 'SMTP Username', defaultVal: '' },
  SMTP_PASSWORD: { name: 'SMTP Password', defaultVal: '' },
  FROM_EMAIL:    { name: 'From Email',    defaultVal: 'noreply@library.org' },
};

function GeneralConfigManager(props) {
  const okapiKy = useOkapiKy();
  const stripes = useStripes();
  const canManage = stripes.hasPerm('ui-reports.configs.manage');
  const callout = useContext(CalloutContext);
  const [initialValues, setInitialValues] = useState({});
  const [configIds, setConfigIds] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const res = await okapiKy.get('reportsConfigs?limit=100&query=cql.allRecords=1').json();
        const configs = res.reportsConfigs || [];

        const ids = {};
        const rawValues = {};
        configs.forEach(c => {
          ids[c.configCode] = c.id;
          rawValues[c.configCode] = c.value ?? CONFIGS[c.configCode]?.defaultVal ?? '';
        });

        setConfigIds(ids);
        setInitialValues({
          maxQueryTimeout:          rawValues.MAX_QUERY_TIMEOUT          || CONFIGS.MAX_QUERY_TIMEOUT.defaultVal,
          reportCacheTtl:           rawValues.REPORT_CACHE_TTL           || CONFIGS.REPORT_CACHE_TTL.defaultVal,
          maxExportRows:            rawValues.MAX_EXPORT_ROWS            || CONFIGS.MAX_EXPORT_ROWS.defaultVal,
          enableQueryValidation:    (rawValues.ENABLE_QUERY_VALIDATION    || 'true') === 'true',
          enableScheduledReports:   (rawValues.ENABLE_SCHEDULED_REPORTS   || 'true') === 'true',
          emailNotifications:       (rawValues.EMAIL_NOTIFICATIONS        || 'true') === 'true',
          dashboardRefreshInterval: rawValues.DASHBOARD_REFRESH_INTERVAL || CONFIGS.DASHBOARD_REFRESH_INTERVAL.defaultVal,
          smtpHost:     rawValues.SMTP_HOST     || '',
          smtpPort:     rawValues.SMTP_PORT     || '587',
          smtpUsername: rawValues.SMTP_USERNAME || '',
          smtpPassword: rawValues.SMTP_PASSWORD || '',
          fromEmail:    rawValues.FROM_EMAIL    || 'noreply@library.org',
        });
      } catch (e) {
        callout.sendCallout({
          type: 'error',
          message: <FormattedMessage id="ui-reports.settings.generalConfig.loadError" />,
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfigs();
  }, []);

  const saveConfig = async (code, value, existingId) => {
    const payload = {
      configCode: code,
      configName: CONFIGS[code].name,
      value: String(value),
      enabled: true,
    };
    if (existingId) {
      await okapiKy.put(`reportsConfigs/${existingId}`, { json: { id: existingId, ...payload } });
    } else {
      await okapiKy.post('reportsConfigs', { json: payload });
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const toSave = {
        MAX_QUERY_TIMEOUT:          values.maxQueryTimeout,
        REPORT_CACHE_TTL:           values.reportCacheTtl,
        MAX_EXPORT_ROWS:            values.maxExportRows,
        ENABLE_QUERY_VALIDATION:    values.enableQueryValidation,
        ENABLE_SCHEDULED_REPORTS:   values.enableScheduledReports,
        EMAIL_NOTIFICATIONS:        values.emailNotifications,
        DASHBOARD_REFRESH_INTERVAL: values.dashboardRefreshInterval,
        SMTP_HOST:     values.smtpHost,
        SMTP_PORT:     values.smtpPort,
        SMTP_USERNAME: values.smtpUsername,
        SMTP_PASSWORD: values.smtpPassword,
        FROM_EMAIL:    values.fromEmail,
      };

      await Promise.all(
        Object.entries(toSave).map(([code, value]) =>
          saveConfig(code, value, configIds[code])
        )
      );

      callout.sendCallout({
        type: 'success',
        message: <FormattedMessage id="ui-reports.settings.generalConfig.saved" />,
      });
    } catch (e) {
      callout.sendCallout({
        type: 'error',
        message: <FormattedMessage id="ui-reports.settings.generalConfig.saveError" />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GeneralConfigForm
      {...props}
      onSubmit={handleSubmit}
      canManage={canManage}
      initialValues={initialValues}
      isLoading={isLoading}
    />
  );
}

export default GeneralConfigManager;
